import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Database, Upload, Download, Loader2, CheckCircle2,
  AlertTriangle, FileSpreadsheet, ChevronLeft, Play, Eye,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";

export default function AdminImportCursos() {
  const [file, setFile] = useState(null);
  const [dryResult, setDryResult] = useState(null);
  const [realResult, setRealResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleDryRun = async () => {
    if (!file) { toast.error("Escolhe primeiro um ficheiro Excel"); return; }
    setBusy(true);
    setRealResult(null);
    try {
      const r = await adminApi.cursosBaseImport(file, { dryRun: true });
      setDryResult(r);
      toast.success("Pré-visualização gerada.");
    } catch (e) {
      toast.error(e.message || "Erro no dry-run");
    } finally { setBusy(false); }
  };

  const handleRealImport = async () => {
    if (!file) { toast.error("Escolhe primeiro um ficheiro Excel"); return; }
    if (!window.confirm(
      "Confirmar IMPORT REAL? Esta acção altera dados em produção:\n\n" +
      `• ${dryResult?.summary?.total_rows || "?"} linhas serão processadas\n` +
      "• Cursos existentes serão actualizados\n" +
      "• Cursos novos serão criados\n" +
      "• Plano de sessões será replaceado para cada curso processado"
    )) return;
    setBusy(true);
    try {
      const r = await adminApi.cursosBaseImport(file, { dryRun: false });
      setRealResult(r);
      const s = r.summary || {};
      toast.success(`Import: ${s.updated} actualizados, ${s.inserted} criados, ${s.sessoes_total} sessões`);
    } catch (e) {
      toast.error(e.message || "Erro no import");
    } finally { setBusy(false); }
  };

  const result = realResult || dryResult;

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="admin-import-cursos">
      <Link to="/admin" className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1 mb-4">
        <ChevronLeft size={12} /> Dashboard
      </Link>

      <header className="mb-6 flex items-start gap-3">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white">
          <Database size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
            Importar Base de Cursos
          </h1>
          <p className="text-sm text-[#4B5563]">
            Faz o upsert dos cursos a partir do ficheiro <code className="text-[11px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 rounded">NOVA-BASE-BD.xlsx</code>{" "}
            (sheets <strong>Público</strong> e <strong>Empresas</strong>). Cada linha cria ou actualiza um curso
            em <code className="text-[11px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 rounded">curso.Tinf_Curso</code>{" "}
            e o respectivo plano de sessões em <code className="text-[11px] bg-[#FAF8F2] border border-[#E6E1D6] px-1 rounded">curso.Trel_PlanoSessao</code>.
          </p>
        </div>
      </header>

      <section className="mb-6 p-5 bg-white border border-[#E6E1D6] rounded-md">
        <h2 className="text-sm font-semibold text-[#1B1B1B] mb-3">1. Ficheiro Excel</h2>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setDryResult(null);
              setRealResult(null);
            }}
            className="text-sm"
            data-testid="import-file-input"
          />
          <a
            href={adminApi.cursosBaseImportTemplateUrl()}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-[#E6E1D6] rounded hover:bg-[#FAF8F2]"
            data-testid="btn-download-template"
          >
            <Download className="w-3.5 h-3.5" /> Descarregar template vazio
          </a>
        </div>
        <p className="text-[12px] text-[#978D78] mt-2">
          Estrutura esperada: <strong>Público</strong> com 60 colunas (idcurso, fxalunos, curso, local, edicao, idproduto,
          cursonosigo, formador, area, entidadeformadora, moradacontrato, sessoes, horario, cargahoraria,
          formacaoinstalacoes, localentidade, datacontrato, horario_PlanoSessao1..18, Sumario1..9 + Sumariodata1..9,
          SumarioFim, datadtp1/3/4, aval1/2). <strong>Empresas</strong> com colunas adicionais (Nome da Empresa,
          NIF da Empresa, Sede, Representada por, na qualidade de).
        </p>
      </section>

      <section className="mb-6 p-5 bg-white border border-[#E6E1D6] rounded-md">
        <h2 className="text-sm font-semibold text-[#1B1B1B] mb-3">2. Processar</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleDryRun}
            disabled={!file || busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-[#A8843C] text-[#A8843C] rounded hover:bg-[#FAF8F2] disabled:opacity-50"
            data-testid="btn-dry-run"
          >
            {busy && dryResult === null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Pré-visualizar (sem alterar BD)
          </button>
          <button
            onClick={handleRealImport}
            disabled={!file || busy || !dryResult}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#A8843C] text-white rounded hover:bg-[#8E6E2A] disabled:opacity-50"
            data-testid="btn-real-import"
          >
            {busy && dryResult ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Executar Import REAL
          </button>
        </div>
        {!dryResult && (
          <p className="text-[12px] text-[#978D78] mt-2">
            Recomendação: corre primeiro a pré-visualização para confirmar quantos cursos serão criados
            vs. actualizados antes de executar o import real.
          </p>
        )}
      </section>

      {result && (
        <section className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden" data-testid="import-result-card">
          <header className="px-5 py-3 border-b border-[#E6E1D6] bg-[#FAF8F2] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1B1B1B] flex items-center gap-2">
              {realResult ? (
                <><CheckCircle2 className="w-4 h-4 text-green-700" /> Import concluído</>
              ) : (
                <><Eye className="w-4 h-4 text-[#A8843C]" /> Pré-visualização</>
              )}
            </h2>
            <SummaryBadges s={result.summary} dryRun={!realResult} />
          </header>

          <div className="p-5 space-y-6">
            {Object.entries(result.details || {}).map(([sheet, rows]) => (
              <SheetResult key={sheet} sheet={sheet} rows={rows} dryRun={!realResult} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}


function SummaryBadges({ s = {}, dryRun }) {
  return (
    <div className="flex flex-wrap gap-2 text-[12px]">
      <Badge color="slate" label="Linhas" value={s.total_rows} />
      {dryRun ? (
        <>
          <Badge color="amber" label="A criar" value={s.would_insert} />
          <Badge color="blue" label="A actualizar" value={s.would_update} />
        </>
      ) : (
        <>
          <Badge color="green" label="Criados" value={s.inserted} />
          <Badge color="blue" label="Actualizados" value={s.updated} />
        </>
      )}
      <Badge color="emerald" label="Sessões" value={s.sessoes_total} />
      {s.errors > 0 && <Badge color="red" label="Erros" value={s.errors} />}
    </div>
  );
}


function Badge({ color, label, value }) {
  const cls = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    blue: "bg-sky-50 border-sky-200 text-sky-700",
    green: "bg-green-50 border-green-200 text-green-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
  }[color] || "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <span className={`px-2 py-0.5 rounded border ${cls}`}>
      <strong>{value ?? "—"}</strong> {label}
    </span>
  );
}


function SheetResult({ sheet, rows, dryRun }) {
  // Agrega contagem por action para o subtítulo
  const counts = rows.reduce((acc, r) => {
    acc[r.action] = (acc[r.action] || 0) + 1;
    return acc;
  }, {});
  return (
    <div data-testid={`sheet-${sheet.toLowerCase()}`}>
      <h3 className="text-sm font-semibold text-[#1B1B1B] mb-2 flex items-center gap-2">
        <FileSpreadsheet className="w-4 h-4 text-[#A8843C]" />
        Sheet: {sheet} <span className="text-[12px] text-[#978D78] font-normal">({rows.length} linhas)</span>
      </h3>
      <div className="flex flex-wrap gap-1.5 mb-3 text-[11px]">
        {Object.entries(counts).map(([action, n]) => (
          <span key={action} className="px-1.5 py-0.5 bg-[#FAF8F2] border border-[#E6E1D6] rounded">
            {action}: <strong>{n}</strong>
          </span>
        ))}
      </div>
      <div className="overflow-x-auto border border-[#E6E1D6] rounded">
        <table className="w-full text-[12px]">
          <thead className="bg-[#FAF8F2] text-[10px] uppercase tracking-wider text-[#978D78]">
            <tr>
              <th className="px-2 py-1.5 text-left w-14">Linha</th>
              <th className="px-2 py-1.5 text-left">Acção</th>
              <th className="px-2 py-1.5 text-left w-24">ID_Curso</th>
              <th className="px-2 py-1.5 text-left w-20">Sessões</th>
              <th className="px-2 py-1.5 text-left">Erros</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.row} className={`border-t border-[#E6E1D6] ${r.errors?.length ? "bg-red-50/40" : ""}`}>
                <td className="px-2 py-1.5 font-mono">{r.row}</td>
                <td className="px-2 py-1.5">
                  <ActionPill action={r.action} dryRun={dryRun} />
                </td>
                <td className="px-2 py-1.5 font-mono">
                  {r.curso_id ? (
                    <Link
                      to={`/admin/cursos/sessoes?curso=${r.curso_id}`}
                      className="text-[#A8843C] hover:underline"
                    >
                      #{r.curso_id}
                    </Link>
                  ) : "—"}
                </td>
                <td className="px-2 py-1.5 text-center">{r.sessoes ?? "—"}</td>
                <td className="px-2 py-1.5 text-red-700">
                  {(r.errors || []).join("; ") || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function ActionPill({ action }) {
  const map = {
    inserted: { tone: "green", label: "Criado" },
    updated: { tone: "blue", label: "Actualizado" },
    would_insert: { tone: "amber", label: "→ Criar" },
    would_update: { tone: "sky", label: "→ Actualizar" },
    skipped: { tone: "slate", label: "Saltado" },
    error: { tone: "red", label: "Erro" },
  };
  const cfg = map[action] || { tone: "slate", label: action };
  const cls = {
    green: "bg-green-50 border-green-200 text-green-700",
    blue: "bg-sky-50 border-sky-200 text-sky-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    red: "bg-red-50 border-red-200 text-red-700",
  }[cfg.tone];
  return (
    <span className={`inline-block px-1.5 py-0.5 border rounded text-[10px] font-semibold ${cls}`}>
      {cfg.label}
    </span>
  );
}
